import React, { useEffect, useRef, useState } from "react";
import { IUser } from "@eagerdog/interfaces";
import moment from "moment";
import { Link } from "react-router-dom";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { localStorageService } from "src/services/local-storage.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import ImageUploader from "src/components/ImageUploader/ImageUploader";

import styles from "./ProfileSummaryCard.module.scss";

const photoUrl = (imageUrl?: string | { value?: string }): string | undefined => {
    if (typeof imageUrl === "string" && imageUrl) return imageUrl;
    if (imageUrl && typeof imageUrl === "object" && imageUrl.value) return imageUrl.value;
    return undefined;
};

interface IProps {
    /** Rendered as a bottom section of the card in the stacked (mobile) layout only. */
    footer?: React.ReactNode;
}

const ProfileSummaryCard: React.FC<IProps> = ({ footer }) => {
    const [user, setUser] = useState<IUser>(helperService.getUser());
    const photoModal = useModal();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [savingPhoto, setSavingPhoto] = useState(false);

    useEffect(() => {
        const refresh = () => setUser(helperService.getUser());
        window.addEventListener("userDataUpdated", refresh);
        return () => window.removeEventListener("userDataUpdated", refresh);
    }, []);

    const [dogsCount, setDogsCount] = useState<number | null>(null);
    const [enteredCount, setEnteredCount] = useState<number | null>(null);
    const [qsCount, setQsCount] = useState<number | null>(null);
    const [titlesCount, setTitlesCount] = useState<number | null>(null);

    useEffect(() => {
        if (!user._id) return;

        apiService.getHomeSummary().then((summary) => {
            setDogsCount(summary.dogs);
            setEnteredCount(summary.events_entered);
            setQsCount(summary.qualifying_runs);
            setTitlesCount(summary.titles_earned);
        }).catch(() => {
            setDogsCount(0);
            setEnteredCount(0);
            setQsCount(0);
            setTitlesCount(0);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user._id]);

    const openPhotoPicker = () => {
        fileInputRef.current?.click();
    };

    const onPhotoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setSelectedFile(file);
        if (!photoModal.isShown) photoModal.toggle();
    };

    const closePhotoModal = () => {
        setSelectedFile(null);
        if (photoModal.isShown) photoModal.toggle();
    };

    const savePhoto = async (file: File | null) => {
        if (!file) return;
        setSavingPhoto(true);
        try {
            const uploaded: any = await apiService.uploadFile(file);
            const image_url = photoUrl(uploaded);
            if (!image_url) throw new Error("Upload did not return a file URL");
            const updatedUser = await apiService.updateSelf({ image_url });
            localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
            setUser(updatedUser);
            window.dispatchEvent(new CustomEvent("userDataUpdated"));
            closePhotoModal();
        } catch {
            toast.show({
                title: "Profile photo",
                content: "Unable to update your profile photo. Please try again.",
                duration: 8000,
                type: "fail",
            });
        } finally {
            setSavingPhoto(false);
        }
    };

    if (!user._id) return null;

    const initial = (user.display_name || user.first_name || "?").charAt(0).toUpperCase();
    const avatarUrl = photoUrl(user.image_url as any);
    const location = [user.address?.city, user.address?.state].filter(Boolean).join(", ");
    const memberSince = user.created_at ? moment(user.created_at).format("YYYY") : null;

    // Short labels fit the 2x2 sidebar tiles; long ones are used in the wider
    // stacked layout (toggled in SCSS).
    const stats: { short: string; long: string; value: number | null }[] = [
        { short: "Dogs", long: "Dogs", value: dogsCount },
        { short: "Events", long: "Events entered", value: enteredCount },
        { short: "Q runs", long: "Qualifying runs", value: qsCount },
        { short: "Titles", long: "Titles earned", value: titlesCount },
    ];

    return (
        <div className={styles.ProfileSummaryCard}>
            <div className={styles.header}>
                <button
                    type="button"
                    className={styles.portrait}
                    onClick={openPhotoPicker}
                    aria-label="Change profile photo"
                >
                    {avatarUrl
                        ? <img src={avatarUrl} alt={user.display_name} />
                        : <span>{initial}</span>}
                </button>
                <div className={styles.identity}>
                    <div className={styles.name}>{user.display_name}</div>
                    {(location || memberSince) && (
                        <div className={styles.meta}>
                            {[location, memberSince ? `Member since ${memberSince}` : null].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
                <Link to="/account" className={styles.viewProfile}>View profile</Link>
            </div>
            <div className={styles.stats}>
                {stats.map(({ short, long, value }) => (
                    <div className={styles.statRow} key={long}>
                        <span className={styles.statValue}>{value ?? "–"}</span>
                        <span className={styles.statLabel}>
                            <span className={styles.labelShort}>{short}</span>
                            <span className={styles.labelLong}>{long}</span>
                        </span>
                    </div>
                ))}
            </div>
            {footer && <div className={styles.footer}>{footer}</div>}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className={styles.fileInput}
                onChange={onPhotoSelected}
            />
            <Modal
                className="profilePhotoModal"
                isShown={photoModal.isShown}
                hide={closePhotoModal}
                modalContent={
                    <div className={styles.photoModal}>
                        {selectedFile && (
                            <ImageUploader
                                label="Profile photo"
                                initialFile={selectedFile}
                                aspectRatio={1}
                                circularCrop
                                maxWidth={400}
                                maxHeight={400}
                                onImageChange={savePhoto}
                                onCancel={closePhotoModal}
                            />
                        )}
                        {savingPhoto && <div className={styles.saving}>Saving photo…</div>}
                    </div>
                }
            />
        </div>
    );
};

export default ProfileSummaryCard;
